/**
 * Recovery for a tab left open across a deploy: its index.html still names
 * the previous build's hashed chunks, which no longer exist on the server,
 * so the next lazily-loaded route fails with "Failed to fetch dynamically
 * imported module". Reloading picks up the new index.html and fixes it.
 *
 * Guarded so a chunk that is genuinely missing (a broken deploy) can't cause
 * a reload loop - a second failure inside the window is left to fail, and
 * reported, instead.
 */

const STORAGE_KEY = 'staleChunkReloadAt'
const GUARD_WINDOW_MS = 10_000

// Chrome/Edge, Firefox, Safari respectively.
const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i

export function isChunkLoadError(error: unknown): boolean {
  const message = (error as { message?: unknown } | null)?.message
  return typeof message === 'string' && CHUNK_LOAD_ERROR.test(message)
}

export function reloadedForStaleChunkRecently(now: number = Date.now()): boolean {
  try {
    return now - Number(sessionStorage.getItem(STORAGE_KEY) ?? 0) < GUARD_WINDOW_MS
  } catch {
    return false
  }
}

/**
 * Reloads onto the latest build - at `target` if given, so a failed route
 * navigation lands where the user was going. Returns false, without
 * reloading, if that was already tried within the guard window.
 */
export function reloadForStaleChunk(target?: string): boolean {
  if (reloadedForStaleChunkRecently()) return false
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()))
  } catch {
    // No way to record the guard, so no way to rule out a reload loop.
    return false
  }
  if (target) window.location.assign(target)
  else window.location.reload()
  return true
}
